import {
  AfterViewInit,
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewContainerRef,
} from '@angular/core';
import { INotesData, NotesComponent } from './notes.component';
import { notesData } from './notes.data';

@Directive({
  selector: '[alNotes]',
})
export class NotesDirective implements AfterViewInit, OnChanges, OnDestroy {
  @Input() alNotes: string = 'addAssistentButton';
  @Input() alNotesIsVisible: boolean = false;
  /**
   * Optional CSS selector for an element to anchor the tooltip to,
   * instead of the directive's host. Useful when the host is just a
   * placeholder and the *real* target is rendered elsewhere — e.g.
   * an `nb-menu` link whose DOM the host can't reach with a template
   * reference. Looked up via `document.querySelector` on every
   * position update so we pick up re-renders automatically.
   */
  @Input() alNotesAnchor?: string;

  @HostListener('mouseenter') onMouseEnter() {}

  componentRef: any;
  private resizeListener = () => this.scheduleUpdate();
  /** Watches the anchor's box (size/position) so the tooltip follows
   *  scrolls/layout shifts without polling. */
  private anchorObserver?: ResizeObserver;
  /** Currently observed anchor element — kept so we can detach the
   *  observer when the selector changes or resolves to a new node. */
  private observedAnchor: Element | null = null;

  constructor(
    protected hostRef: ElementRef,
    private viewContainerRef: ViewContainerRef
  ) {
    window.addEventListener('resize', this.resizeListener);
  }

  /** Resolve the anchor element: either the configured selector match
   *  or the directive's host as fallback. */
  private resolveAnchor(): Element {
    if (this.alNotesAnchor) {
      const el = document.querySelector(this.alNotesAnchor);
      if (el) return el;
    }
    return this.hostRef.nativeElement;
  }

  /** (Re-)attach the ResizeObserver to whichever element is currently
   *  the anchor. Idempotent — no-op when the anchor hasn't changed. */
  private syncAnchorObserver(anchor: Element): void {
    if (this.observedAnchor === anchor) return;
    this.anchorObserver?.disconnect();
    this.observedAnchor = anchor;
    if (typeof ResizeObserver === 'undefined') return;
    this.anchorObserver = new ResizeObserver(() => this.scheduleUpdate());
    this.anchorObserver.observe(anchor);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['alNotesIsVisible'] || changes?.['alNotesAnchor']) {
      // Defer measurement so the layout is settled after Angular change detection
      this.scheduleUpdate();
    }
  }

  ngAfterViewInit() {
    this.componentRef = this.viewContainerRef.createComponent(NotesComponent);
    // Append to body so position:fixed works relative to the viewport,
    // not an ancestor scroll container.
    document.body.appendChild(this.componentRef.location.nativeElement);
    this.scheduleUpdate();
  }

  /** Double rAF: waits for two browser paint cycles to ensure layout is complete. */
  private scheduleUpdate() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.updateComponentData();
      });
    });
  }

  ngOnDestroy() {
    window.removeEventListener('resize', this.resizeListener);
    this.anchorObserver?.disconnect();
    const el = this.componentRef?.location?.nativeElement;
    if (el && el.parentNode === document.body) {
      document.body.removeChild(el);
    }
    this.componentRef?.destroy();
  }

  updateComponentData() {
    const anchor = this.resolveAnchor();
    this.syncAnchorObserver(anchor);
    const rect = (anchor as HTMLElement).getBoundingClientRect();

    // Read positioning flags from the matching `notesData` entry:
    //   - anchorEdge 'bottom' → tooltip Y from anchor's bottom edge
    //     (e.g. dock under a container regardless of inner layout)
    //   - centerOnAnchor → tooltip X from anchor's midpoint, paired with
    //     transform: translateX(-50%) in the component for clean centring
    const noteMeta = (notesData as any)[this.alNotes];
    const baseTop =
      noteMeta?.anchorEdge === 'bottom' ? rect.top + rect.height : rect.top;
    const baseLeft = noteMeta?.centerOnAnchor
      ? rect.left + rect.width / 2
      : rect.left;

    const data: INotesData = {
      name: this.alNotes,
      // Hide if the anchor is requested but couldn't be resolved —
      // a stale tooltip floating over an unrelated element is worse
      // than no tooltip at all.
      isVisible:
        this.alNotesIsVisible &&
        (!this.alNotesAnchor || anchor !== this.hostRef.nativeElement),
      position: {
        top: baseTop,
        left: baseLeft,
        width: rect.width,
        height: rect.height,
      },
      centered: !!noteMeta?.centerOnAnchor,
    };

    if (!this.componentRef?.instance) {
      return;
    }

    this.componentRef.instance.data = data;
    this.componentRef.instance.onDataChange(data);
  }
}
